import React from "react";
import { Image } from "@v2/components/display/Image/Image";
import { CloseIcon } from "@v2/icons";
import { cn } from "@gettactic/helpers/src";
import { getContrastTextColor } from "@gettactic/helpers/src/colors/getContrastTextColor";

export interface ChipProps {
    className?: string;
    isDisabled?: boolean;
    pictureUrl?: string | null;
    pictureFallback?: React.ReactNode;
    onClick?: (event: React.MouseEvent) => void;
    onHide?: () => void;
    onCancel?: (event: React.MouseEvent) => void;
    borderColor?: string;
    fallbackColor?: string;
}

const classes = {
    Wrapper:
        "relative flex items-center gap-[6px] overflow-hidden rounded-[30px] border-1 border-neutral-100 bg-neutral-50 p-1 text-neutral-1000 hover:cursor-pointer hover:border-neutral-200 select-none",
    Image: "h-6 w-6 overflow-hidden rounded-full",
    Text: "overflow-hidden text-nowrap text-[13px]",
    ActionsWrapper:
        "absolute right-0 pr-1 pl-4 top-0 bottom-0 flex items-center justify-center",
    Button: "text-neutral-700 hover:text-neutral-1000",
    ButtonDisabled: "hover:text-neutral-700",
    Icon: "size-4",
    DisabledWrapper: "opacity-60 hover:cursor-default",
    Fallback:
        "flex h-6 w-6 items-center justify-center bg-neutral-300 rounded-full overflow-hidden text-xs text-neutral-700"
};

export const Chip: React.FC<React.PropsWithChildren<ChipProps>> = ({
                                                                       className,
                                                                       isDisabled = false,
                                                                       pictureUrl,
                                                                       children,
                                                                       onClick,
                                                                       onHide,
                                                                       onCancel,
                                                                       pictureFallback,
                                                                       borderColor,
                                                                       fallbackColor
                                                                   }) => {
    const hasActions = !!onCancel || !!onHide;

    return (
        <div
            className={cn(
                classes.Wrapper,
                isDisabled && classes.DisabledWrapper,
                className
            )}
            onClick={onClick}
            style={borderColor ? { borderColor } : undefined}
        >
            {pictureUrl ? (
                <div>
                    <Image src={pictureUrl} className={classes.Image} />
                </div>
            ) : pictureFallback ? (
                <div
                    className={classes.Fallback}
                    style={
                        fallbackColor
                            ? {
                                backgroundColor: fallbackColor,
                                color: getContrastTextColor(fallbackColor)
                            }
                            : undefined
                    }
                >
                    {pictureFallback}
                </div>
            ) : undefined}
            <div
                className={cn(
                    classes.Text,
                    !pictureUrl && !pictureFallback ? "ml-1" : ""
                )}
            >
                {children}
            </div>
            {hasActions && (
                <>
                    <div className={"w-4"} />

                    <div
                        className={classes.ActionsWrapper}
                        style={{
                            // TODO: Make it a tailwind variable
                            background:
                                "linear-gradient(270deg, #F0F4F7 66.43%, rgba(240, 244, 247, 0) 100%)"
                        }}
                    >
                        {onCancel ? (
                            <button
                                className={cn(
                                    classes.Button,
                                    isDisabled && classes.ButtonDisabled
                                )}
                                disabled={isDisabled}
                                type={"button"}
                                onClick={onCancel}
                            >
                                <CloseIcon className={classes.Icon} />
                            </button>
                        ) : undefined}
                    </div>
                </>
            )}
        </div>
    );
};
